import React, { useEffect, useRef, useState } from 'react';
import { COVER_SLIDE_INTERVAL_MS } from '../../services/predictionService';

// ============================================
// PredictionCoverSlider – Karten-Cover im Tippspiel (Marcel 2026-09-22):
// erst das Tippspiel-Bild, nach 5 s automatisch die Sponsoren-Bilder,
// Endlosschleife, weiche Überblendung. Nur ein Slide → kein Timer.
// Slides kommen aus predictionService.coverSlidesFor(game).
// ============================================

interface Props {
  slides: string[];
  alt: string;
  className?: string;
  /** Anzeige ohne Slides (z. B. Icon-Fläche) */
  fallback?: React.ReactNode;
  /** Wird gerufen, wenn ein Slide sichtbar wird (initial + jeder Wechsel) – z. B. Sponsor-Impressionen */
  onSlideShown?: (index: number) => void;
}

const PredictionCoverSlider: React.FC<Props> = ({ slides, alt, className = '', fallback = null, onSlideShown }) => {
  const [index, setIndex] = useState(0);
  const count = slides.length;
  const shownRef = useRef(onSlideShown);
  shownRef.current = onSlideShown;

  useEffect(() => {
    if (count > 0) shownRef.current?.(index);
  }, [index, count]);

  useEffect(() => {
    setIndex(0);
    if (count < 2) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), COVER_SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [count]);

  if (count === 0) return <div className={`relative overflow-hidden ${className}`}>{fallback}</div>;

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid="prediction-cover-slider" data-active-index={index}>
      {slides.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt={i === 0 ? alt : ''}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === index ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={i !== index}
        />
      ))}
      {count > 1 && (
        <div className="absolute bottom-1.5 right-2 flex items-center gap-1" aria-hidden="true">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-3.5 bg-[#3fced1]' : 'w-1.5 bg-white/60'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PredictionCoverSlider;
